/**
 * Notification Preferences Component
 */

import React, { useState, useEffect } from 'react';
import { useToastStore } from '../../stores/toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getNotificationPreferences, 
  updateNotificationPreferences,
  type NotificationPreferences as NotificationPreferencesType,
  type NotificationPreferencesUpdate,
} from '../../api/notifications';
import { Button } from '../UI';
import { Card } from '../UI';
import { Save, X } from 'lucide-react';

export interface NotificationPreferencesProps {
  projectId?: string;
  onClose?: () => void;
}

export function NotificationPreferences({ projectId, onClose }: NotificationPreferencesProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [localPreferences, setLocalPreferences] = useState<NotificationPreferencesUpdate | null>(null);

  // Fetch preferences
  const { data: preferences, isLoading, error, isError } = useQuery({
    queryKey: ['notifications', 'preferences', projectId],
    queryFn: async () => {
      try {
        console.log('[NotificationPreferences] Fetching preferences for projectId:', projectId);
        const result = await getNotificationPreferences(projectId);
        console.log('[NotificationPreferences] Fetched preferences:', result);
        
        // Validate result
        if (!result) {
          console.error('[NotificationPreferences] Result is null/undefined');
          throw new Error('Preferences data is undefined');
        }
        
        if (!result.id && !result.user_id) {
          console.error('[NotificationPreferences] Invalid preferences structure:', result);
          throw new Error('Invalid preferences response structure');
        }
        
        return result;
      } catch (err: any) {
        console.error('[NotificationPreferences] Error fetching preferences:', {
          error: err,
          message: err?.message,
          response: err?.response?.data,
          status: err?.response?.status,
          projectId,
        });
        throw err;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });

  // Use default preferences if none exist (but don't block rendering)
  // This handles cases where the API returns undefined or the query fails
  const effectivePreferences: NotificationPreferencesType = (preferences && typeof preferences === 'object' && (preferences.id || preferences.user_id)) ? preferences : {
    id: '',
    user_id: '',
    project_id: projectId,
    channel_preferences: {},
    notification_preferences: {},
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00:00',
    quiet_hours_end: '08:00:00',
    quiet_hours_timezone: 'UTC',
    quiet_hours_override_critical: true,
    max_notifications_per_hour: 10,
    max_notifications_per_day: 50,
    digest_enabled: false,
    digest_frequency: undefined,
    digest_time: '09:00:00',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  
  // Log what we're using
  if (import.meta.env.DEV) {
    console.log('[NotificationPreferences] Using preferences:', {
      hasPreferences: !!preferences,
      usingDefaults: !preferences,
      effectivePreferences,
      isLoading,
      isError,
    });
  }

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (prefs: NotificationPreferencesUpdate) => 
      updateNotificationPreferences(prefs, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
      setLocalPreferences(null); // Reset local changes after save
      addToast({
        type: 'success',
        message: 'Notification preferences saved successfully',
      });
    },
    onError: (error: any) => {
      addToast({
        type: 'error',
        message: error?.response?.data?.detail || error?.message || 'Failed to save notification preferences',
      });
    },
  });

  const handleSave = () => {
    if (localPreferences) {
      updateMutation.mutate(localPreferences);
    } else {
      // No changes to save
      addToast({
        type: 'info',
        message: 'No changes to save',
      });
    }
  };

  const updateChannelPreference = (channel: string, key: string, value: any) => {
    setLocalPreferences((prev) => {
      const currentChannelPrefs = prev?.channel_preferences || effectivePreferences?.channel_preferences || {};
      const currentChannel = currentChannelPrefs[channel] || {};
      return {
        ...prev,
        channel_preferences: {
          ...currentChannelPrefs,
          [channel]: {
            ...currentChannel,
            [key]: value,
          },
        },
      };
    });
  };

  const updateNotificationTypePreference = (type: string, key: string, value: any) => {
    setLocalPreferences((prev) => {
      const currentNotifPrefs = prev?.notification_preferences || effectivePreferences?.notification_preferences || {};
      const currentType = currentNotifPrefs[type] || {};
      return {
        ...prev,
        notification_preferences: {
          ...currentNotifPrefs,
          [type]: {
            ...currentType,
            [key]: value,
          },
        },
      };
    });
  };

  // Show loading state only when actually loading (not when data is being processed)
  if (isLoading && !preferences) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
          Loading preferences...
        </div>
      </div>
    );
  }

  // Log error details for debugging
  if (isError) {
    console.error('[NotificationPreferences] Query error:', {
      error,
      isError,
      preferences,
      projectId,
      queryKey: ['notifications', 'preferences', projectId],
    });
  }

  // If there's an error but we have no preferences, show error but allow fallback to defaults
  // This way users can still interact with the UI even if the initial load fails
  if (isError && !preferences && error) {
    console.warn('[NotificationPreferences] Error loading preferences, using defaults:', error);
    // Don't block rendering - we'll use defaults below
  }

  const currentPrefs = localPreferences || effectivePreferences;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Notification Preferences
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {projectId ? 'Project-specific preferences' : 'Global preferences'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Error warning banner if using defaults due to error */}
        {isError && !preferences && error && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Could not load saved preferences
                </h3>
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>
                    {error instanceof Error ? error.message : 'An error occurred while loading preferences. Default settings are being used.'}
                  </p>
                  <p className="mt-1">
                    You can still configure your preferences below. Changes will be saved when you click "Save Changes".
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Channel Preferences */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Channel Preferences
          </h2>
          <div className="space-y-4">
            {['email', 'sms', 'whatsapp', 'inapp', 'push'].map((channel) => {
              const channelPrefs = currentPrefs.channel_preferences?.[channel] || {};
              return (
                <div key={channel} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-slate-900 dark:text-slate-100 capitalize">
                      {channel === 'inapp' ? 'In-App' : channel.toUpperCase()}
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Receive notifications via {channel}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={channelPrefs.enabled !== false}
                    onChange={(e) => updateChannelPreference(channel, 'enabled', e.target.checked)}
                    className="w-5 h-5"
                  />
                </div>
              );
            })}
          </div>
        </Card>

        {/* Email Notifications */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Email Notifications
          </h2>
          <div className="space-y-4">
            {[
              { 
                key: 'new_opportunity', 
                label: 'New opportunities matching your filters',
                description: 'Get notified when new opportunities match your saved filters'
              },
              { 
                key: 'proposal_review_request', 
                label: 'Proposal review requests',
                description: 'Notifications when you receive review requests'
              },
              { 
                key: 'review_feedback_received', 
                label: 'Review feedback received',
                description: 'Get notified when reviewers provide feedback'
              },
              { 
                key: 'system_updates', 
                label: 'System updates and announcements',
                description: 'Important system updates and announcements'
              },
            ].map((item) => {
              const typePrefs = currentPrefs.notification_preferences?.[item.key] || {};
              const emailEnabled = currentPrefs.channel_preferences?.email?.enabled !== false;
              return (
                <div key={item.key} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {item.label}
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {item.description}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={typePrefs.enabled !== false && emailEnabled}
                      onChange={(e) => {
                        updateNotificationTypePreference(item.key, 'enabled', e.target.checked);
                        if (e.target.checked) {
                          // Ensure email channel is enabled
                          updateChannelPreference('email', 'enabled', true);
                        }
                      }}
                      className="w-5 h-5 mt-1"
                    />
                  </div>
                  {typePrefs.enabled !== false && emailEnabled && (
                    <div className="ml-0 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-slate-600 dark:text-slate-400">Frequency:</label>
                        <select
                          value={typePrefs.frequency || 'immediate'}
                          onChange={(e) => updateNotificationTypePreference(item.key, 'frequency', e.target.value)}
                          className="text-xs px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                          <option value="immediate">Immediate</option>
                          <option value="daily">Daily Digest</option>
                          <option value="weekly">Weekly Digest</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* In-App Notifications */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            In-App Notifications
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
              <div>
                <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Enable/disable notification sound
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Play sound when new notifications arrive
                </p>
              </div>
              <input
                type="checkbox"
                checked={currentPrefs.channel_preferences?.inapp?.sound_enabled !== false}
                onChange={(e) => updateChannelPreference('inapp', 'sound_enabled', e.target.checked)}
                className="w-5 h-5"
              />
            </div>
            <div className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
              <div>
                <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Show notification badges
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Display unread count badges
                </p>
              </div>
              <input
                type="checkbox"
                checked={currentPrefs.channel_preferences?.inapp?.badges_enabled !== false}
                onChange={(e) => updateChannelPreference('inapp', 'badges_enabled', e.target.checked)}
                className="w-5 h-5"
              />
            </div>
            <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
              <label className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2 block">
                Notification display duration (seconds)
              </label>
              <input
                type="number"
                min="3"
                max="30"
                value={currentPrefs.channel_preferences?.inapp?.display_duration || 5}
                onChange={(e) => updateChannelPreference('inapp', 'display_duration', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                How long notifications stay visible (3-30 seconds)
              </p>
            </div>
            <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
              <label className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2 block">
                Notification grouping preferences
              </label>
              <select
                value={currentPrefs.channel_preferences?.inapp?.grouping || 'by_type'}
                onChange={(e) => updateChannelPreference('inapp', 'grouping', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              >
                <option value="none">No Grouping</option>
                <option value="by_type">Group by Type</option>
                <option value="by_time">Group by Time</option>
                <option value="by_project">Group by Project</option>
              </select>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                How to group notifications in the notification center
              </p>
            </div>
          </div>
        </Card>

        {/* Notification Type Preferences */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            All Notification Types
          </h2>
          <div className="space-y-4">
            {[
              'job_completion',
              'report_comment',
              'report_status_change',
              'new_opportunity',
              'keyword_recommendation',
              'industry_news',
            ].map((type) => {
              const typePrefs = currentPrefs.notification_preferences?.[type] || {};
              return (
                <div key={type} className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </label>
                    <input
                      type="checkbox"
                      checked={typePrefs.enabled !== false}
                      onChange={(e) => updateNotificationTypePreference(type, 'enabled', e.target.checked)}
                      className="w-5 h-5"
                    />
                  </div>
                  {typePrefs.enabled !== false && (
                    <div className="ml-6 space-y-2">
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-600 dark:text-slate-400">Channels:</label>
                        {['email', 'sms', 'whatsapp', 'inapp'].map((channel) => (
                          <label key={channel} className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={(typePrefs.channels || []).includes(channel)}
                              onChange={(e) => {
                                const channels = typePrefs.channels || [];
                                const newChannels = e.target.checked
                                  ? [...channels, channel]
                                  : channels.filter((c) => c !== channel);
                                updateNotificationTypePreference(type, 'channels', newChannels);
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-xs capitalize">{channel === 'inapp' ? 'In-App' : channel}</span>
                          </label>
                        ))}
                      </div>
                      {['new_opportunity', 'keyword_recommendation', 'industry_news'].includes(type) && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-slate-600 dark:text-slate-400">Digest:</label>
                          <select
                            value={typePrefs.digest || 'none'}
                            onChange={(e) => updateNotificationTypePreference(type, 'digest', e.target.value === 'none' ? undefined : e.target.value)}
                            className="text-xs px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
                          >
                            <option value="none">None</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Quiet Hours */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Quiet Hours
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Enable Quiet Hours
              </label>
              <input
                type="checkbox"
                checked={currentPrefs.quiet_hours_enabled || false}
                onChange={(e) => {
                  setLocalPreferences((prev) => ({ 
                    ...prev, 
                    quiet_hours_enabled: e.target.checked 
                  }));
                }}
                className="w-5 h-5"
              />
            </div>
            {currentPrefs.quiet_hours_enabled && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">Start Time</label>
                    <input
                      type="time"
                      value={effectivePreferences.quiet_hours_start 
                        ? (typeof effectivePreferences.quiet_hours_start === 'string' && effectivePreferences.quiet_hours_start.includes('T')
                            ? effectivePreferences.quiet_hours_start.split('T')[1]?.substring(0, 5) || '22:00'
                            : effectivePreferences.quiet_hours_start.substring(0, 5) || '22:00')
                        : '22:00'}
                      onChange={(e) => {
                        // Convert HH:MM to HH:MM:SS format for API
                        const timeValue = e.target.value + ':00';
                        setLocalPreferences((prev) => ({ ...prev, quiet_hours_start: timeValue }));
                      }}
                      className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">End Time</label>
                    <input
                      type="time"
                      value={effectivePreferences.quiet_hours_end 
                        ? (typeof effectivePreferences.quiet_hours_end === 'string' && effectivePreferences.quiet_hours_end.includes('T')
                            ? effectivePreferences.quiet_hours_end.split('T')[1]?.substring(0, 5) || '08:00'
                            : effectivePreferences.quiet_hours_end.substring(0, 5) || '08:00')
                        : '08:00'}
                      onChange={(e) => {
                        // Convert HH:MM to HH:MM:SS format for API
                        const timeValue = e.target.value + ':00';
                        setLocalPreferences((prev) => ({ ...prev, quiet_hours_end: timeValue }));
                      }}
                      className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-600 dark:text-slate-400">Time Zone</label>
                  <select
                    value={currentPrefs.quiet_hours_timezone || effectivePreferences.quiet_hours_timezone || 'UTC'}
                    onChange={(e) => setLocalPreferences((prev) => ({ ...prev, quiet_hours_timezone: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentPrefs.quiet_hours_override_critical !== false}
                    onChange={(e) => setLocalPreferences((prev) => ({ ...prev, quiet_hours_override_critical: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <label className="text-sm text-slate-600 dark:text-slate-400">
                    Allow critical notifications during quiet hours
                  </label>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Frequency Limits */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Frequency Limits
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Max Notifications Per Hour
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={currentPrefs.max_notifications_per_hour || effectivePreferences.max_notifications_per_hour || 10}
                onChange={(e) => setLocalPreferences((prev) => ({ ...prev, max_notifications_per_hour: parseInt(e.target.value) }))}
                className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Max Notifications Per Day
              </label>
              <input
                type="number"
                min="1"
                max="500"
                value={currentPrefs.max_notifications_per_day || effectivePreferences.max_notifications_per_day || 50}
                onChange={(e) => setLocalPreferences((prev) => ({ ...prev, max_notifications_per_day: parseInt(e.target.value) }))}
                className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700"
              />
            </div>
          </div>
        </Card>

        {/* Digest Preferences */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Digest Preferences
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Enable Digests
              </label>
              <input
                type="checkbox"
                checked={currentPrefs.digest_enabled || false}
                onChange={(e) => setLocalPreferences((prev) => ({ ...prev, digest_enabled: e.target.checked }))}
                className="w-5 h-5"
              />
            </div>
            {currentPrefs.digest_enabled && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    Digest Frequency
                  </label>
                  <select
                    value={currentPrefs.digest_frequency || effectivePreferences.digest_frequency || 'daily'}
                    onChange={(e) => setLocalPreferences((prev) => ({ ...prev, digest_frequency: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    Digest Time
                  </label>
                  <input
                    type="time"
                    value={effectivePreferences.digest_time 
                      ? (typeof effectivePreferences.digest_time === 'string' && effectivePreferences.digest_time.includes('T')
                          ? effectivePreferences.digest_time.split('T')[1]?.substring(0, 5) || '09:00'
                          : effectivePreferences.digest_time.substring(0, 5) || '09:00')
                      : '09:00'}
                    onChange={(e) => {
                      // Convert HH:MM to HH:MM:SS format for API
                      const timeValue = e.target.value + ':00';
                      setLocalPreferences((prev) => ({ ...prev, digest_time: timeValue }));
                    }}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700"
                  />
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

